/**
 * Created: 2026-10-24
 * Initial commit: Add coachingSessionStats query for the 课程统计 page
 *
 * 课程统计 page: the completed coaching sessions of the current site in a date
 * range, each with its students' income and its court-hours' cost. Used by
 * both the admin and the coach app.
 * Updated: 2026-10-24 — A footer sums the income and court cost; clicking a
 * row opens the AppointmentDialog.
 * Updated: 2026-10-24 — Each student shows their hours × hour price, one line
 * per entitlement drawn on.
 * Updated: 2026-10-24 — The footer sums the court-hours used.
 * Updated: 2026-10-24 — The footer sums the hours taught.
 * Updated: 2026-10-24 — An 导出 Excel button downloads the sessions shown.
 * Updated: 2026-10-29 — A 教练 dropdown filters the sessions by coach.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { DataGridPro, type GridColDef, GridFooterContainer } from '@mui/x-data-grid-pro'
import type { DateRange } from '@mui/x-date-pickers-pro/models'
import { format } from 'date-fns'
import { useState } from 'react'
import AppointmentDialog from '../components/AppointmentDialog'
import DashboardRangePicker, { defaultRange } from '../components/DashboardRangePicker'
import UserAvatar from '../components/UserAvatar'
import {
  CoachingSessionStatsDocument,
  type CoachingSessionStatsQuery,
} from '../gql/graphql'
import { useRole } from '../hooks'
import {
  durationHours,
  exportSessionStats,
  formatHour,
  formatYuan,
  timeLines,
} from './sessionStatsExcel'

gql`
  query CoachingSessionStats($siteId: UUID!, $startDate: Date!, $endDate: Date!) {
    coachingSessionStats(siteId: $siteId, startDate: $startDate, endDate: $endDate) {
      sessionId
      course {
        id
        name
      }
      coach {
        id
        openid
        name
        avatar
        gender
      }
      attendees {
        student {
          id
          openid
          name
          avatar
          gender
        }
        headcount
        draws {
          hours
          hourPrice
        }
        income
      }
      slots {
        date
        hour
        courtPrice
        court {
          id
          name
        }
      }
      income
      slotsCost
    }
  }
`

declare module '@mui/x-data-grid-pro' {
  interface FooterPropsOverrides {
    count: number
    hours: number
    income: number
    courtHours: number
    slotsCost: number
  }
}

type StatRow = CoachingSessionStatsQuery['coachingSessionStats'][number]

function TotalFooter({
  count,
  hours,
  income,
  courtHours,
  slotsCost,
}: {
  count: number
  hours: number
  income: number
  courtHours: number
  slotsCost: number
}) {
  return (
    <GridFooterContainer sx={{ justifyContent: 'flex-end', gap: 3, px: 2 }}>
      <Typography variant="subtitle1">共 {count} 节</Typography>
      <Typography variant="subtitle1">时长合计: {hours} 小时</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
        课程收入合计: {formatYuan(income)}
      </Typography>
      <Typography variant="subtitle1">场地使用合计: {courtHours} 场时</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
        场地成本合计: {formatYuan(slotsCost)}
      </Typography>
    </GridFooterContainer>
  )
}

export default function SessionStatsPage() {
  const { currentSite } = useRole()
  const [range, setRange] = useState<DateRange<Date>>(defaultRange)
  const [dialogId, setDialogId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  // '' shows every coach's sessions
  const [coachId, setCoachId] = useState('')

  const [startDate, endDate] = range
  const { data, loading, error } = useQuery(CoachingSessionStatsDocument, {
    variables: {
      siteId: currentSite?.id ?? '',
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : '',
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : '',
    },
    skip: !currentSite || !startDate || !endDate,
    fetchPolicy: 'network-only',
  })

  const columns: GridColDef<StatRow>[] = [
    {
      field: '__rowNumber',
      headerName: '#',
      width: 50,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    {
      field: 'time',
      headerName: '上课时间',
      width: 220,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {timeLines(row.slots).map((line) => (
            <Typography key={line} variant="body2">
              {line}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      field: 'duration',
      headerName: '时长',
      width: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_value, row) => `${durationHours(row.slots)}小时`,
    },
    {
      field: 'course',
      headerName: '课程',
      width: 150,
      sortable: false,
      headerAlign: 'center',
      valueGetter: (_value, row) => row.course.name,
    },
    {
      field: 'coach',
      headerName: '教练',
      width: 160,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ row }) => <UserAvatar user={row.coach} showMobile={false} />,
    },
    {
      field: 'attendees',
      headerName: '学员 · 课时 × 课时单价',
      width: 300,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {row.attendees.map((a) => (
            <Box
              key={a.student.openid}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <UserAvatar user={a.student} showMobile={false} />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {a.headcount > 1 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {a.headcount}人
                  </Typography>
                )}
                {a.draws.map((d, i) => (
                  <Typography
                    // A student's draws have no id of their own.
                    // biome-ignore lint/suspicious/noArrayIndexKey: static list
                    key={i}
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                  >
                    {d.hours}课时 × {formatYuan(d.hourPrice)}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: 'income',
      headerName: '课程收入',
      width: 110,
      sortable: false,
      align: 'right',
      headerAlign: 'center',
      valueGetter: (_value, row) => formatYuan(row.income),
    },
    {
      field: 'slots',
      headerName: '场地使用',
      flex: 1,
      minWidth: 260,
      sortable: false,
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {row.slots.map((s) => (
            <Typography key={`${s.court.id}-${s.date}-${s.hour}`} variant="body2">
              {formatHour(s.hour)} · {s.court.name} ·{' '}
              {s.courtPrice == null ? '未定价' : formatYuan(s.courtPrice)}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      field: 'slotsCost',
      headerName: '场地成本',
      width: 110,
      sortable: false,
      align: 'right',
      headerAlign: 'center',
      valueGetter: (_value, row) => formatYuan(row.slotsCost),
    },
  ]

  const allRows = data?.coachingSessionStats ?? []
  // The coaches to pick from are those with sessions in the range.
  const coachNames = new Map<string, string>()
  for (const { coach } of allRows) {
    if (coach.id) coachNames.set(coach.id, coach.name ?? '未知')
  }
  const coaches = [...coachNames]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
  const rows = coachId ? allRows.filter((r) => r.coach.id === coachId) : allRows
  const totalHours = rows.reduce((sum, r) => sum + durationHours(r.slots), 0)
  const totalIncome = rows.reduce((sum, r) => sum + Number(r.income), 0)
  // Every slot is one court-hour; a session on two courts counts both.
  const totalCourtHours = rows.reduce((sum, r) => sum + r.slots.length, 0)
  const totalSlotsCost = rows.reduce((sum, r) => sum + Number(r.slotsCost), 0)

  const handleExport = async () => {
    setExporting(true)
    try {
      const period = [startDate, endDate]
        .map((d) => (d ? format(d, 'yyyy-MM-dd') : ''))
        .join('_')
      await exportSessionStats(
        rows,
        {
          hours: totalHours,
          income: totalIncome,
          courtHours: totalCourtHours,
          slotsCost: totalSlotsCost,
        },
        `课程统计_${currentSite?.name ?? ''}_${period}.xlsx`,
      )
    } catch (err) {
      message.error(`导出失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setExporting(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}
      >
        <Typography variant="h4">课程统计</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="session-stats-coach-label" shrink>
            教练
          </InputLabel>
          <Select
            labelId="session-stats-coach-label"
            label="教练"
            notched
            displayEmpty
            value={coachId}
            onChange={(e) => setCoachId(e.target.value)}
          >
            <MenuItem value="">全部教练</MenuItem>
            {/* keeps the picked coach valid when the range has none of theirs */}
            {coachId && !coaches.some((c) => c.id === coachId) && (
              <MenuItem value={coachId} sx={{ display: 'none' }}>
                —
              </MenuItem>
            )}
            {coaches.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DashboardRangePicker range={range} onRangeChange={setRange} />
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          disabled={loading || exporting || rows.length === 0}
          onClick={handleExport}
        >
          导出 Excel
        </Button>
      </Box>

      {error && <Alert severity="error">加载课程统计失败: {error.message}</Alert>}

      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={(row: StatRow) => row.sessionId}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnMenu
        density="compact"
        getRowHeight={() => 'auto'}
        onRowClick={({ row }: { row: StatRow }) => setDialogId(row.sessionId)}
        slots={{ footer: TotalFooter }}
        slotProps={{
          footer: {
            count: rows.length,
            hours: totalHours,
            income: totalIncome,
            courtHours: totalCourtHours,
            slotsCost: totalSlotsCost,
          },
        }}
        localeText={{ noRowsLabel: '暂无已结束的课程' }}
        sx={{
          flex: 1,
          minHeight: 0,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 1,
          },
        }}
      />

      <AppointmentDialog sessionId={dialogId} onClose={() => setDialogId(null)} />
    </Box>
  )
}
